/**
 * Prefetch registry — Feb 2026 (Fluidity · Instant Triggers)
 *
 * Single source of truth for "warm this query up when the user is about
 * to navigate" logic. Shared between:
 *   - BottomTabBar     (mobile tabs)
 *   - DispatchTopBar   (breadcrumb domain link + ⌘K search launcher)
 *   - GlobalUserSearch (⌘K palette result rows)
 *
 * Trigger semantics:
 *   - `onPointerDown` / `onTouchStart` — fires the millisecond a finger
 *     touches the screen (before release), so the fetch is racing the
 *     tap animation instead of the tap-then-render pipeline.
 *   - `onPointerEnter` / `onMouseEnter` — desktop hover warmup.
 *   - `onFocus` — keyboard nav.
 *
 * `prefetchQuery` is idempotent — if the cache is fresh (within
 * staleTime) it returns immediately without a network hit, so we can
 * fire from many triggers without worrying about storms.
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Domain-key → { queryKey, fetch } for BottomTabBar tabs & Breadcrumb
// domain crumbs. Only high-traffic domains listed; cheap pages just
// render on click.
export const DOMAIN_PREFETCH_MAP = {
  ops: {
    queryKey: ['dispatch', 'orders'],
    fetch: () => axios.get(`${API_URL}/api/orders`, { withCredentials: true }).then((r) => r.data || []),
  },
  growth: {
    queryKey: ['dispatch', 'launch-stats'],
    fetch: () => axios.get(`${API_URL}/api/admin/launch-stats`, { withCredentials: true }).then((r) => r.data),
  },
};

// Prefetch a single domain's landing query. Idempotent no-op if the
// domain has no registered prefetch.
export function prefetchDomain(queryClient, domainKey) {
  const cfg = DOMAIN_PREFETCH_MAP[domainKey];
  if (!cfg || !queryClient) return;
  queryClient.prefetchQuery({ queryKey: cfg.queryKey, queryFn: cfg.fetch });
}

// Prefetch the dispatch orders list (used by ORDER rows in ⌘K palette
// and by the search launcher button — the palette itself pulls from
// /api/orders and the destination page reads ['dispatch','orders']).
export function prefetchOrders(queryClient) {
  if (!queryClient) return;
  queryClient.prefetchQuery({
    queryKey: ['dispatch', 'orders'],
    queryFn: () => axios.get(`${API_URL}/api/orders`, { withCredentials: true }).then((r) => r.data || []),
  });
}

// Prefetch the dispatch products list (used by PRODUCT rows in ⌘K
// palette; destination InventoryPage reads ['dispatch','products']).
export function prefetchProducts(queryClient) {
  if (!queryClient) return;
  queryClient.prefetchQuery({
    queryKey: ['dispatch', 'products'],
    queryFn: () => axios.get(`${API_URL}/api/products`, { withCredentials: true }).then((r) => {
      const d = r.data;
      return Array.isArray(d) ? d : (d?.items || d?.products || []);
    }),
  });
}

// Consumer helper — attach these three handlers to any nav element to
// get the "instant tap" behavior. Keeps prop lists tidy in JSX.
export function instantPrefetchHandlers(fn) {
  return {
    onPointerDown: fn,
    onTouchStart: fn,
    onPointerEnter: fn,
    onFocus: fn,
  };
}
